/**
 * Splits a string containing emoji characters into an array of individual emojis.
 * This handles complex emoji sequences like skin tone modifiers, flags, and ZWJ sequences.
 *
 * @param emojiString - A string containing one or more emoji characters
 * @returns An array of individual emoji strings
 *
 * @example
 * ```ts
 * splitEmojiString('❄️🔥') // ['❄️', '🔥']
 * splitEmojiString('🆔🔪🍷') // ['🆔', '🔪', '🍷']
 * splitEmojiString('') // []
 * splitEmojiString('🥶👨‍💻🇺🇸') // ['🥶', '👨‍💻', '🇺🇸']
 * ```
 */
export function splitEmojiString(emojiString: string): string[] {
  if (!emojiString || typeof emojiString !== 'string') {
    return []
  }

  // Trim and check for empty string
  const trimmed = emojiString.trim()
  if (!trimmed) {
    return []
  }

  // Use the Intl.Segmenter API if available (modern browsers)
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    // TypeScript doesn't know about Segmenter yet, so we need to cast
    const IntlWithSegmenter = Intl as any
    const segmenter = new IntlWithSegmenter.Segmenter('en', { granularity: 'grapheme' })
    const segments = Array.from(segmenter.segment(trimmed), (segment: any) => segment.segment)

    // Filter to only include emoji characters, excluding regular text
    return segments.filter((segment: string) => {
      // Basic emoji detection - check if the segment contains emoji characters
      const emojiRegex = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u
      return emojiRegex.test(segment)
    })
  }

  // Fallback: Use a comprehensive regular expression to match emoji sequences
  // This regex matches individual emojis including:
  // - Regional Indicator Symbols (flags)
  // - General emoji blocks
  // - Miscellaneous Technical symbols
  // - Dingbats
  // We need to match each emoji separately, not as a continuous sequence
  const emojiRegex = /[\u{1F1E0}-\u{1F1FF}]{2}|[\u{1F000}-\u{1FAFF}]\u{FE0F}?|[\u{2600}-\u{26FF}]\u{FE0F}?|[\u{2700}-\u{27BF}]\u{FE0F}?/gu

  const matches = trimmed.match(emojiRegex)
  return matches || []
}

/**
 * Normalises emoji items from the hsFields object.
 * Handles both the old array format and the new string format.
 *
 * @param emojiValue - The emoji value from hsFields, can be string, string[], or undefined
 * @returns An array of individual emoji strings
 *
 * @example
 * ```ts
 * normaliseEmojiItems('❄️🔥') // ['❄️', '🔥']
 * normaliseEmojiItems('') // []
 * normaliseEmojiItems(undefined) // []
 * ```
 */
export function normaliseEmojiItems(emojiValue?: string): string[] {
  if (!emojiValue) {
    return []
  }

  return splitEmojiString(emojiValue)
}
